/* eslint-disable i18next/no-literal-string */
import type * as React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";

/**
 * Ported from bouncieweb's `LandingPageComponents/FAQAccordionHum.js`.
 *
 * DELIBERATE DEVIATION (user-approved): the original was a hand-rolled `<div>`
 * accordion with an `onClick` handler and a hardcoded `max-height` cap that
 * `useWindowSize()` set to 402px below 1024px and 175px at/above it. Those caps
 * clip question 3, whose answer is two paragraphs. This port composes the
 * existing Radix accordion instead, which sizes its content from
 * `--radix-accordion-content-height`, so every answer opens to its full height.
 * `type="single" collapsible` reproduces the original's one-open-at-a-time plus
 * click-to-close behavior, and keyboard/ARIA support comes free (the original's
 * click-only div would also fail `jsx-a11y`).
 *
 * Cosmetic differences that fall out of the shared accordion, both accepted: the
 * chevron rotates over 200ms rather than the original's 400ms, and it is
 * vertically centered against multi-line questions rather than top-aligned.
 *
 * Second deviation, matching the sibling ports: question 9's external link gains
 * `rel="noopener noreferrer"`, which the original omitted alongside its
 * `target="_blank"`.
 *
 * Original breakpoint map (theme/mediaQueries): lg 1024px -> lg:
 */

const faqs: { question: string; answer: React.ReactNode }[] = [
  {
    question:
      "What happens to my Hum by Verizon service after December 31, 2025?",
    answer: `Verizon has announced that all Hum services, including diagnostics, location tracking,
            crash response, roadside assistance, Wi-Fi hotspot, and more, will cease on December 31, 2025.
            After that date, the Hum app and platform will no longer be active.`,
  },
  {
    question: "Can I transfer my Hum driving history to Bouncie?",
    answer: (
      <>
        Bouncie is developing a data import feature that will allow Hum users to
        bring over driving history, trip logs, and driving insights into the
        Bouncie platform. This ensures your important records stay with you.{" "}
        <i>
          (Feature availability will depend on technical and privacy
          requirements.)
        </i>
      </>
    ),
  },
  {
    question:
      "What core features did Hum offer, and what does Bouncie offer in comparison?",
    answer: (
      <>
        Hum+ offered features like vehicle diagnostic alerts, maintenance
        reminders, location tracking, speed and boundary alerts, crash response,
        and roadside assistance. HumX added extras such as an in-vehicle Wi-Fi
        hotspot, a Bluetooth speaker with calling capability, and an emergency
        button.
        <br />
        <br />
        Bouncie provides real-time location tracking, trip history, driving
        insights, impact detection, and essential diagnostics. While Bouncie does
        not include a built-in Wi-Fi hotspot, in-car speaker, or emergency
        button, it delivers reliable, affordable, and easy-to-use technology that
        helps you stay connected to your vehicle every day.
      </>
    ),
  },
  {
    question:
      "Will I lose access to all features (like crash alerts or roadside assistance) if I switch to Bouncie?",
    answer: `You will keep the core features that Bouncie supports, including real-time tracking, trip history,
            and vehicle diagnostics. Because Verizon is ending the Hum system, Hum-specific features such as
            roadside assistance, Wi-Fi, and speaker-based emergency services will no longer be available.
            Bouncie ensures you maintain connectivity and key insights that matter most.`,
  },
  {
    question: "Is my vehicle compatible with Bouncie?",
    answer: (
      <>
        Bouncie, like Hum, is compatible with most vehicles built from 1996 and
        newer. Some electric vehicles may not be supported. You can{" "}
        <a href="#obd-compatibility-scroll-target">check compatibility</a> by
        entering your vehicle make, model, and year on Bouncie&rsquo;s website
        before ordering.
      </>
    ),
  },
  {
    question: "How long does it take to get started with Bouncie?",
    answer: `Setup is fast and simple. Activate your device through the app by entering your vehicle details and the
            IMEI number, then plug the device into your vehicle’s OBD2 port. After a short 10-minute drive, calibration
            completes, and tracking begins immediately.`,
  },
  {
    question: "Do I need a contract to use Bouncie?",
    // The original joined these two sentences with a `\`-continuation, which
    // keeps the following indentation and no newline; both collapse to the same
    // single space in the rendered text.
    answer:
      "No contract is required. Bouncie has no activation fees and operates on a flexible month-to-month plan. You can start and stop your service anytime.",
  },
  {
    question: "Can I monitor driver behavior like speeding or idling?",
    answer: `Yes. Bouncie provides detailed driving insights, including speeding alerts, idle time, trip distance,
            and start and stop times. You can review driver behavior in the app or set notifications for specific events,
            such as speeding or hard braking.`,
  },
  {
    question: "What makes Bouncie a good replacement for Hum by Verizon?",
    answer: (
      <>
        Bouncie gives Hum users continuity, convenience, and peace of mind. It
        keeps you connected to your vehicle with real-time location, trip
        insights, and driving behavior reports, all within an easy-to-use app.
        It&rsquo;s one of the highest-rated connected car solutions and&nbsp;
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.bouncie.com/blog/hum-by-verizon-is-shutting-down-heres-why-bouncie-is-the-best-replacement-for-your-connected-car-experience"
        >
          a trusted next step for drivers after Hum.
        </a>
      </>
    ),
  },
];

export default function HumFaq() {
  return (
    // Wrapper
    <div className="mt-8 mb-12">
      {/* Faqs */}
      <Accordion type="single" collapsible className="flex flex-col gap-4">
        {faqs.map((faq, index) => (
          // Faq
          <AccordionItem
            key={faq.question}
            value={`q${index + 1}`}
            className="overflow-hidden rounded-lg border-b-0 bg-[#E1EDFD]"
          >
            {/* Question — the original's plain body weight and color, not the
                shared accordion's bold heading treatment. */}
            <AccordionTrigger data-sid={`hum-faq-trigger:${index + 1}`} className="gap-2 p-6 pr-3 text-left font-normal tracking-normal text-inherit lg:pr-6">
              {faq.question}
            </AccordionTrigger>
            {/* Answer */}
            <AccordionContent className="max-w-[820px] px-6 pt-0 pb-6 pl-10 text-sm text-[#1E1F22]">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
